import React from 'react';
import { Row } from 'antd';

import { FileToolsetSettingsInput, LLMSettings, ProviderModel, ToolsetModel } from '../../../../../../gamma-api';
import IbIcon from '../../../../../components/common/IbIcon';
import IbNumberInput from '../../../../../components/common/IbNumberInput';
import IbTypography from '../../../../../components/common/IbTypography';
import ModelSelector from '../../../../../components/ModelSelector';
import IbSwitch from '../../../../../components/common/IbSwitch';
import { getTranslateFunc } from '../../../../../utils/stringUtil';
import { tryParseIntValue } from '../../../../../utils/numberUtil';
import PromptEditorExpandable from '../../../../../components/common/PromptEditorExpandable';
import { PromptLanguage } from '../../../../../components/common/PromptEditor/const';
import { CodeEditorSize } from '../../../../../components/common/CodeEditorExpandable/types';

import {
  PARAMETERS_CLASS_NAME,
  SECTION_CLASS_NAME,
  SECTION_FORM_CLASS_NAME,
  SECTION_FORM_ITEM_CLASS_NAME,
  SECTION_TITLE_CLASS_NAME,
} from './const';

export interface IFileToolsParametersProps {
  editToolset: ToolsetModel;
  providers: ProviderModel[];
  providersLoading: boolean;
  onChange: (toolset: ToolsetModel) => void;
}

const FileToolsParameters: React.FC<IFileToolsParametersProps> = ({
  editToolset,
  providers,
  providersLoading,
  onChange,
}) => {
  const translate = getTranslateFunc('Agent: Toolset: Modal: Settings: File:');
  const fileToolsetSettings = editToolset.settings as FileToolsetSettingsInput;

  const onMaxContentTokenCountChange = (value: string | number) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        read_file_tool_settings: {
          ...fileToolsetSettings.read_file_tool_settings,
          max_content_token_count: tryParseIntValue(value) ?? 0,
        },
      },
    } as ToolsetModel);
  };

  const onLLMChange = (value: LLMSettings) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        describe_image_tool_settings: {
          ...fileToolsetSettings.describe_image_tool_settings,
          llm_settings: value,
        },
      },
    } as ToolsetModel);
  };

  const onImageDescribePromptChange = (value: string) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        describe_image_tool_settings: {
          ...fileToolsetSettings.describe_image_tool_settings,
          default_prompt: value,
        },
      },
    } as ToolsetModel);
  };

  const onAllowPromptOverrideChange = (value: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        describe_image_tool_settings: {
          ...fileToolsetSettings.describe_image_tool_settings,
          allow_prompt_override: value,
        },
      },
    } as ToolsetModel);
  };

  const onWriteFileEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        write_file_tool_settings: {
          ...fileToolsetSettings.write_file_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onReadFileEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        read_file_tool_settings: {
          ...fileToolsetSettings.read_file_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  const onDescribeImageEnabledChange = (checked: boolean) => {
    onChange({
      ...editToolset,
      settings: {
        ...editToolset.settings,
        describe_image_tool_settings: {
          ...fileToolsetSettings.describe_image_tool_settings,
          enabled: checked,
        },
      },
    } as ToolsetModel);
  };

  return (
    <div className={PARAMETERS_CLASS_NAME}>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={fileToolsetSettings.write_file_tool_settings.enabled}
            size="small"
            onChange={onWriteFileEnabledChange}
          />
          <IbIcon iconName="file-editing" size={20} />
          <IbTypography.Paragraph strong>{translate('Write file tool: Title')}</IbTypography.Paragraph>
        </div>
        <IbTypography.Paragraph type="secondary">{translate('Write file tool: Description')}</IbTypography.Paragraph>
      </div>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={fileToolsetSettings.read_file_tool_settings.enabled}
            size="small"
            onChange={onReadFileEnabledChange}
          />
          <IbIcon iconName="file-text" size={20} />
          <IbTypography.Paragraph strong>{translate('Read file tool: Title')}</IbTypography.Paragraph>
        </div>
        {fileToolsetSettings.read_file_tool_settings.enabled && (
          <div className={SECTION_FORM_CLASS_NAME}>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph strong type="secondary">
                {translate('Read file tool: Max content length: Title')}
              </IbTypography.Paragraph>
              <IbNumberInput
                min={0}
                precision={0}
                value={fileToolsetSettings.read_file_tool_settings.max_content_token_count}
                onChange={onMaxContentTokenCountChange}
              />
            </div>
          </div>
        )}
      </div>
      <div className={SECTION_CLASS_NAME}>
        <div className={SECTION_TITLE_CLASS_NAME}>
          <IbSwitch
            checked={fileToolsetSettings.describe_image_tool_settings.enabled}
            size="small"
            onChange={onDescribeImageEnabledChange}
          />
          <IbIcon iconName="image-files" size={20} />
          <IbTypography.Paragraph strong>{translate('Describe image tool: Title')}</IbTypography.Paragraph>
        </div>
        {fileToolsetSettings.describe_image_tool_settings.enabled && (
          <div className={SECTION_FORM_CLASS_NAME}>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph strong type="secondary">
                {translate('Describe image tool: LLM: Title')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">
                {translate('Describe image tool: LLM: Description')}
              </IbTypography.Paragraph>
              <ModelSelector.LLM
                loading={providersLoading}
                providers={providers}
                settings={fileToolsetSettings.describe_image_tool_settings.llm_settings}
                onChange={onLLMChange}
              />
            </div>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <IbTypography.Paragraph strong type="secondary">
                {translate('Describe image tool: Prompt: Title')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">
                {translate('Describe image tool: Prompt: Description')}
              </IbTypography.Paragraph>
              <PromptEditorExpandable
                promptLanguage={PromptLanguage.PLAIN_TEXT}
                size={CodeEditorSize.MD}
                value={fileToolsetSettings.describe_image_tool_settings.default_prompt}
                onChange={onImageDescribePromptChange}
              />
            </div>
            <div className={SECTION_FORM_ITEM_CLASS_NAME}>
              <Row>
                <IbSwitch
                  checked={fileToolsetSettings.describe_image_tool_settings.allow_prompt_override}
                  size="small"
                  onChange={onAllowPromptOverrideChange}
                />
                <IbTypography.Paragraph strong type="secondary">
                  {translate('Describe image tool: Allow prompt override: Title')}
                </IbTypography.Paragraph>
              </Row>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileToolsParameters;
